import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react';
import './OrderDetails.css';
import { ORDER_STATUS } from "../../utils/orderStatus";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

//const validStatuses = ['Order Placed', 'Order Accepted', 'Work in Progress', 'Shipped'];
/*const validStatuses = [
    { value: "placed", label: "Order Placed" },
    { value: "accepted", label: "Order Accepted" },
    { value: "inprogress", label: "Work in Progress" },
    { value: "shipped", label: "Shipped" },
    { value: "cancelled", label: "Cancelled" }
];*/
const OrderDetails = ({ orderId, onBack, setToastMessage, setToast }) => {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openDropdown, setOpenDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchOrderDetails();
    }, [orderId]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    const fetchOrderDetails = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/orders/${orderId}`);
            const data = await res.json();
            if (data.success) {
                setOrder(data.data);
            } else {
                setToastMessage("Failed to load order details");
                setToast(true);
                setTimeout(() => setToast(false), 3000);
            }
        } catch (err) {
            console.error("Error fetching order details:", err);
            setToastMessage("Error loading order details");
            setToast(true);
            setTimeout(() => setToast(false), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        setOpenDropdown(false);
        try {
            const res = await fetch(`${API_URL}/orders/${orderId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await res.json();
            if (data.success) {
                setOrder(prev => ({ ...prev, status: newStatus }));
                setToastMessage(`Status updated to ${newStatus}`);
                setToast(true);
                setTimeout(() => setToast(false), 3000);
            } else {
                setToastMessage("Failed to update status");
                setToast(true);
                setTimeout(() => setToast(false), 3000);
            }
        } catch (err) {
            console.error("Error updating status:", err);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { 
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };


    if (loading || !order) {
        return (
            <div className="order-details-container" style={{display: 'flex', justifyContent: 'center', paddingTop: '50px'}}>
                <Loader2 className="spin-icon" size={32} color="#db2777" />
            </div>
        );
    }

const currentStatusClass = ORDER_STATUS[order.status]?.className;
    let bgStyle = { backgroundColor: '#f8fafc' }; // default
    if (currentStatusClass === 'status-placed') bgStyle.backgroundColor = '#fdf2f8';
    if (currentStatusClass === 'status-accepted') bgStyle.backgroundColor = '#eff6ff';
    if (currentStatusClass === 'status-progress') bgStyle.backgroundColor = '#fefce8';
    if (currentStatusClass === 'status-shipped') bgStyle.backgroundColor = '#f0fdf4';
 if (currentStatusClass === 'status-cancelled') bgStyle.backgroundColor = ' #fee2e2';
    return (
        <div className="order-details-container fade-in">
            <div className="back-link" onClick={onBack}>
                <ArrowLeft size={16} />
                <span>Back to All Orders</span>
            </div>

            <div className="order-details-header">
                <div className="header-left">
                    <h2 className="order-id-title">Order #{order.id.split('-')[0]}</h2>
                    <p className="order-date-text">Placed on {formatDateTime(order.created_at)}</p>
                </div>
                <div className="header-right">
                    <p className="total-label">Total Amount</p>
                    <p className="total-value">₹{Number(order.total_amount).toLocaleString('en-IN')}</p>
                </div>
            </div>

            <div className="status-section" style={bgStyle}>
                <div className="status-label-group">
                    <span className="current-status-label">Current Status</span>
                    <span className={`order-status-badge ${ORDER_STATUS[order.status]?.className}`}>
                        {ORDER_STATUS[order.status]?.label}
                    </span>
                </div>

                <div className="status-dropdown-container" ref={dropdownRef}>
                    <button 
                        className="status-dropdown-button"
                        onClick={() => setOpenDropdown(!openDropdown)}
                    >
                        Update Status <ChevronDown size={14} />
                    </button>
                    
                    {openDropdown && (
                        <div className="status-dropdown-menu" style={{ right: 0 }}>

{Object.keys(ORDER_STATUS).map(key => (
    <div
        key={key}
        className={`status-dropdown-item ${order.status === key ? "active" : ""}`}
        onClick={() => handleStatusUpdate(key)}
    >
        {ORDER_STATUS[key].label}
    </div>
))}

                        </div>
                    )}
                </div>
            </div>

            <div className="info-section">
                <h3 className="section-title">Customer Information</h3>
                <div className="customer-info-grid">
                    <div className="info-card">
                        <span className="info-label">Name</span>
                        <div className="info-value">{order.delivery_name}</div>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Phone</span>
                        <div className="info-value">{order.delivery_phone || order.user_phone || 'N/A'}</div>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Email</span>
                        <div className="info-value">{order.delivery_email || order.user_email || 'N/A'}</div>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Pincode</span>
                        <div className="info-value">{order.delivery_pincode || 'N/A'}</div>
                    </div>
                    <div className="info-card full-width">
                        <span className="info-label">Delivery Address</span>
                        <div className="info-value">
                            {order.delivery_address}<br/>
                            {order.delivery_city && order.delivery_city !== 'Unknown' ? `${order.delivery_city}, ` : ''}
                            {order.delivery_state && order.delivery_state !== 'Unknown' ? `${order.delivery_state}, ` : ''}
                            {order.delivery_country && order.delivery_country !== 'Unknown' ? order.delivery_country : ''}
                        </div>
                    </div>
                </div>
            </div>

            <div className="info-section">
                <h3 className="section-title">Order Items</h3>
                <div className="items-list">
                    {order.items && order.items.map(item => (
                        <div key={item.id} className="item-card">
                            <div className="item-left">
                                <img 
                                    src={item.product_image || '/uploads/placeholder.jpg'} 
                                    alt={item.product_name} 
                                    className="item-thumb"
                                    onError={(e) => { e.target.src = '/uploads/placeholder.jpg'; }}
                                />
                                <div className="item-details">
                                    <h4 className="item-name">{item.product_name}</h4>
                                    <p className="item-qty">Qty: {item.quantity}</p>
                                </div>
                            </div>
                            <div className="item-price">
                                ₹{Number(item.price).toLocaleString('en-IN')}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default OrderDetails;
